import { motion, SVGMotionProps, Variants } from 'motion/react'
import { useScale } from '../utils/scaleUtils'

interface Props extends SVGMotionProps<SVGSVGElement> {
  checked: boolean
  /** default 500ms */
  duration?: number
  size?: number
}

export default function AnimatedCheckmark({ checked, size = 24, duration = 500, ...rest }: Props) {
  const scale = useScale()
  const scaledSize = size * scale

  const { baseDuration, stemDuration } = getTransitions(duration)

  return (
    <motion.svg
      width={scaledSize}
      height={scaledSize}
      strokeWidth={2}
      viewBox="0 0 24 24"
      stroke="white"
      strokeLinecap="round"
      {...rest}
    >
      <motion.line
        {...base}
        animate={checked ? 'visible' : 'hidden'}
        initial="hidden"
        transition={{ duration: baseDuration / 1000 }}
        variants={variants}
      />
      <motion.line
        {...stem}
        animate={checked ? 'visible' : 'hidden'}
        initial="hidden"
        transition={{ delay: baseDuration / 1000, duration: stemDuration / 1000 }}
        variants={variants}
      />
    </motion.svg>
  )
}

const variants: Variants = {
  hidden: { pathLength: 0, display: 'none' },
  visible: { pathLength: 1, display: 'inherit' },
}

const base = { x1: 2, y1: 12, x2: 10, y2: 19 }
const stem = { x1: base.x2, y1: base.y2, x2: 22, y2: 5 }

function getTransitions(duration: number) {
  const baseLength = Math.sqrt((base.x2 - base.x1) ** 2 + (base.y2 - base.y1) ** 2)
  const stemLength = Math.sqrt((stem.x2 - stem.x1) ** 2 + (stem.y2 - stem.y1) ** 2)
  const totalLength = baseLength + stemLength
  const baseDuration = (baseLength / totalLength) * duration
  const stemDuration = (stemLength / totalLength) * duration

  return { baseDuration, stemDuration }
}
